import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconScan } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useWorkstationDeviceStatus } from "../../hooks/useWorkstationDeviceStatus";
import {
  deviceBridgeService,
  type BridgeResponse,
} from "../../services/deviceBridge.service";
import {
  documentScanningService,
  type BackendScanOutcome,
} from "../../services/documentScanning.service";
import { WorkstationSelectorModal } from "../bridge";

interface DocumentScannerStatusPanelProps {
  title?: string;
  onFileDetected?: (file: File) => void;
  /** Callback that fires when scanner readiness changes. */
  onScannerReadyChange?: (ready: boolean) => void;
}

/**
 * Converts base64-encoded scan data from a bridge event into a File object.
 */
function base64ToFile(
  base64: string,
  fileName: string,
  mimeType: string
): File {
  const byteString = atob(base64);
  const bytes = new Uint8Array(byteString.length);
  byteString.split("").forEach((char, i) => {
    bytes[i] = char.charCodeAt(0);
  });
  return new File([bytes], fileName, { type: mimeType });
}

/**
 * Scanner status panel for document scanning via Bridge.
 * Checks real backend workstation + device connection status.
 * Listens for document_scanned events from the bridge and converts
 * them into File objects passed to onFileDetected.
 * Clicking opens the workstation selector if not configured or offline.
 */
export default function DocumentScannerStatusPanel({
  title = "Document Scanner",
  onFileDetected,
  onScannerReadyChange,
}: DocumentScannerStatusPanelProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const status = useWorkstationDeviceStatus("document_scanner");

  const handleBackendScanOutcome = useCallback(
    (outcome: BackendScanOutcome) => {
      if (outcome.status === "success") {
        onFileDetected?.(outcome.file);
        return;
      }
      notifications.show({
        title: "Scan failed",
        message: outcome.message,
        color: "red",
        autoClose: 8000,
      });
    },
    [onFileDetected]
  );

  useEffect(
    () =>
      documentScanningService.onBackendScanOutcome(handleBackendScanOutcome),
    [handleBackendScanOutcome]
  );

  // Subscribe to bridge messages and listen for scan events
  useEffect(() => {
    if (!onFileDetected) return undefined;

    const handler = (message: BridgeResponse) => {
      // Normalize: local bridge uses `type`, backend relay uses `messageType`
      const raw = message as unknown as Record<string, unknown>;
      const eventType = (raw.type ?? raw.messageType ?? "") as string;

      if (
        eventType !== "document_scanned" &&
        eventType !== "DOCUMENT_SCANNED" &&
        eventType !== "device:event"
      )
        return;

      // For device:event wrapper, check inner eventType
      const payload = message.payload ?? {};
      const innerEventType = (payload.eventType ?? eventType) as string;
      if (
        innerEventType !== "document_scanned" &&
        innerEventType !== "DOCUMENT_SCANNED"
      )
        return;

      // Extract scan data — could be at payload level or nested in payload.data
      const data = (payload.data ?? payload) as Record<string, unknown>;
      const imageData =
        (data.imageData as string) ??
        (data.base64 as string) ??
        (data.base64Data as string);
      if (!imageData) return;

      const format = (data.format as string) ?? "png";
      const mimeType = format === "pdf" ? "application/pdf" : `image/${format}`;
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 23); // Include milliseconds for uniqueness
      const fileName = `scan_${timestamp}.${format}`;

      const file = base64ToFile(imageData, fileName, mimeType);
      onFileDetected(file);
    };

    const unsubscribe = deviceBridgeService.onMessage(handler);
    return unsubscribe;
  }, [onFileDetected]);

  const statusColor = useMemo((): string => {
    if (!status.hasWorkstation) return "var(--mantine-color-orange-6)";
    if (status.isLoading) return "var(--mantine-color-gray-5)";
    if (!status.workstationOnline) return "var(--mantine-color-red-6)";
    if (!status.device) return "var(--mantine-color-orange-6)";
    if (!status.deviceConnected) return "var(--mantine-color-red-6)";
    return "var(--mantine-color-green-6)";
  }, [
    status.hasWorkstation,
    status.isLoading,
    status.workstationOnline,
    status.device,
    status.deviceConnected,
  ]);

  const statusMessage = useMemo((): string => {
    if (!status.hasWorkstation) return "Click to configure your workstation";
    if (status.isLoading) return "Checking connection status…";
    if (!status.workstationOnline)
      return `${status.workstationName || "Workstation"} is offline`;
    if (!status.device) return "No document scanner on this workstation";
    if (!status.deviceConnected) return `${status.device.name} is disconnected`;
    return `${status.device.name} on ${status.workstationName}`;
  }, [
    status.hasWorkstation,
    status.isLoading,
    status.workstationOnline,
    status.workstationName,
    status.device,
    status.deviceConnected,
  ]);

  const isScannerReady = useMemo(
    () =>
      status.hasWorkstation &&
      status.workstationOnline &&
      status.deviceConnected,
    [status.hasWorkstation, status.workstationOnline, status.deviceConnected]
  );

  // Notify parent when scanner readiness changes
  useEffect(() => {
    onScannerReadyChange?.(!!isScannerReady);
  }, [isScannerReady, onScannerReadyChange]);

  const handleOpen = useCallback(() => {
    setSelectorOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setSelectorOpen(false);
  }, []);

  return (
    <>
      <UnstyledButton
        onClick={handleOpen}
        style={{ cursor: "pointer", width: "100%" }}
      >
        <Paper p="md" withBorder>
          <Stack gap="xs">
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              gap="xs"
            >
              <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
                <IconScan size={16} style={{ flexShrink: 0 }} />
                <Text fw={500} size="sm" truncate>
                  {title}
                </Text>
              </Group>
              <Tooltip label={statusMessage} withArrow withinPortal>
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: statusColor,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Group>
            <Text size="xs" c="dimmed" truncate>
              {statusMessage}
            </Text>
          </Stack>
        </Paper>
      </UnstyledButton>

      <WorkstationSelectorModal opened={selectorOpen} onClose={handleClose} />
    </>
  );
}
